import { useCallback, useEffect, useState } from 'react';
import {
    AlertTriangle, Check, Clock, FileText, Info, Loader2, Printer, Square, CheckSquare,
} from 'lucide-react';
import {
    ApiCallError, getAppointmentChecklist, setChecklistStepTick, fileHandoutOnLoad,
    getDocumentUrl,
    type AppointmentChecklistDto, type RequirementStatusDto, type HandoutForLoadDto,
    type PrintItemDto,
} from '@/api/client';

/**
 * WHAT THIS LOAD IS NOT FINISHED WITHOUT (#317).
 *
 * Pete, 2026-08-27: *"Almost like a checklist before they depart kind of thing."*
 *
 * ---- ⛔⛔ THE ONE THING THIS SCREEN MUST NOT DO ---------------------------
 *
 * ⚠️ IT MUST NOT SHOW A RED MARK AGAINST SOMETHING THAT CANNOT EXIST YET. A signed
 * BOL does not exist until the driver signs it, and a checklist showing "signed BOL
 * ✗" at six in the morning is not reporting a problem — it is reporting Tuesday.
 *
 * ⛔ #269 IS THE PRECEDENT AND IT COST A REAL COMPLAINT. A "no safety ack" chip
 * rendered on the absence of a timestamp at a site where nobody is ever asked, so
 * it showed on every card, forever, for a task that did not exist. The colour that
 * means "do something" was spent on nothing, and a scheduler learns to skip past
 * the line that also carries the one that mattered.
 *
 * ⭐ SO THERE ARE FOUR STATES AND THREE DIFFERENT WEIGHTS OF INK. "Met" is quiet,
 * "waiting for the driver" is quiet, "not wired yet" is quiet, and only a genuine
 * gap is loud — louder still when it will hold the truck.
 *
 * ---- ⭐ WHY IT EXPLAINS WHICH LIST IT USED --------------------------------
 *
 * With four scope levels — company, facility, client, carrier — *"why is this load
 * asking for a packing slip?"* gets asked in week one. ⚠️ A rule nobody can trace
 * is a rule nobody trusts, and the OCR Ranking screen already set the precedent of
 * naming its source. This one names the winning list AND the lists it displaced.
 *
 * ---- ⛔ ONLY STEPS HAVE A CHECKBOX ----------------------------------------
 *
 * ⚠️ AND THAT ASYMMETRY IS THE DESIGN, NOT AN OMISSION. A document requirement is
 * answered by the documents actually on the load; a checkbox beside it would be a
 * SECOND truth that drifts — somebody voids the BOL, the tick stays green, the load
 * releases. A step has no artefact to speak for it, so a person's word is all there
 * is, and that is worth storing with their name against it.
 */

const STATE_STYLE: Record<string, { icon: JSX.Element; text: string; row: string }> = {
    met: {
        icon: <Check className="mt-px h-3.5 w-3.5 shrink-0 text-emerald-600" />,
        text: 'text-muted-foreground',
        row: '',
    },
    /* ⚠️ A CLOCK, NOT A CROSS. The mark itself has to say "not yet" rather than
       "wrong", because a scheduler reads the icon before the sentence. */
    awaitingDriver: {
        icon: <Clock className="mt-px h-3.5 w-3.5 shrink-0 text-sky-600" />,
        text: 'text-sky-800',
        row: '',
    },
    notWired: {
        icon: <Info className="mt-px h-3.5 w-3.5 shrink-0 text-muted-foreground" />,
        text: 'text-muted-foreground',
        row: '',
    },
    missing: {
        icon: <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0 text-amber-600" />,
        text: 'text-amber-800',
        row: '',
    },
};

/** ⛔ A blocking gap is drawn louder than an advisory one. See the header. */
const BLOCKING_ROW = 'rounded border border-amber-300 bg-amber-50 px-1.5 py-1';

export function DepartureChecklistPanel({
    appointmentId,
    facilityCode,
    getToken,
    onChanged,
    hideWhenEmpty = false,
}: {
    appointmentId: string | null;
    /**
     * ⭐ Render NOTHING when no checklist is configured, instead of the dashed
     * "nothing is configured" box. Default false, deliberately — see the long note
     * beside the guard that reads this. Only the Edit dialog passes it.
     */
    hideWhenEmpty?: boolean;
    /**
     * ⛔ #317 STEP 5 — REQUIRED TO FILE A HANDOUT, and absent means the buttons are
     * not offered. The facility decides the document rules the copy is filed
     * under — allowed extensions, size limit, naming template, retention — so
     * guessing it would file the copy under another site's policy.
     */
    facilityCode?: string | null;
    getToken: () => Promise<string>;
    /** ⭐ The tile's paperwork badge depends on this — see hasBlockingGap. */
    onChanged?: (hasBlockingGap: boolean) => void;
}) {
    const [data, setData] = useState<(AppointmentChecklistDto & { reason?: string }) | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [busyItem, setBusyItem] = useState<string | null>(null);
    /** ⭐ #317/5 — the handout currently being copied onto the load. */
    const [busyHandout, setBusyHandout] = useState<string | null>(null);
    /**
     * ⭐ THE PRINT PACK, COLLAPSED BY DEFAULT. It is a working list somebody opens
     * when she is about to collect paper, not something to read on every glance.
     */
    const [showPrint, setShowPrint] = useState(false);
    /**
     * ⛔ WHICH DOCUMENTS HAVE BEEN OPENED, so nothing is silently skipped.
     *
     * ⚠️ "OPENED", NOT "PRINTED", AND THE WORDING SAYS SO. Nothing here can see a
     * printer; a tick means somebody clicked, which is the honest claim.
     *
     * ⭐ HELD IN STATE AND DELIBERATELY NOT STORED. It is a working aid for the
     * next two minutes, not a record of anything — storing it would be exactly the
     * second truth the derive-do-not-store rule above refuses.
     */
    const [opened, setOpened] = useState<Set<string>>(new Set());
    const [showScope, setShowScope] = useState(false);

    const load = useCallback(async () => {
        if (!appointmentId) return;
        setLoading(true);
        try {
            const r = await getAppointmentChecklist(await getToken(), appointmentId);
            setData(r);
            setError(null);
            onChanged?.(r.assessment?.hasBlockingGap ?? false);
        } catch (err) {
            /* ⛔ THE ERROR IS SHOWN, NOT SWALLOWED. Degrading to "no requirements"
               would be worse than an error here: an empty checklist is
               indistinguishable from a satisfied one, and that is the false green
               this whole feature exists to avoid. */
            setError(err instanceof ApiCallError
                ? err.details.error
                : "Could not read this appointment's checklist.");
        } finally {
            setLoading(false);
        }
    }, [appointmentId, getToken, onChanged]);

    useEffect(() => { void load(); }, [load]);

    async function toggleStep(item: RequirementStatusDto) {
        const id = item.requirement.requirementId;
        setBusyItem(id);
        try {
            const r = await setChecklistStepTick(
                await getToken(), appointmentId as string, id, item.state !== 'met');
            setData(r);
            setError(null);
            onChanged?.(r.assessment.hasBlockingGap);
        } catch (err) {
            setError(err instanceof ApiCallError ? err.details.error : 'Could not save that tick.');
        } finally {
            setBusyItem(null);
        }
    }

    async function doFile(h: HandoutForLoadDto) {
        if (!appointmentId || !facilityCode) return;
        setBusyHandout(h.handout.handoutId);
        try {
            await fileHandoutOnLoad(await getToken(), appointmentId, h.handout.handoutId, facilityCode);
            /* ⛔ RELOAD RATHER THAN PATCH. Filing a handout puts a real document on
               the appointment, which can satisfy a `standing` requirement — so the whole
               checklist may have changed, not just this row. */
            await load();
        } catch (err) {
            setError(err instanceof ApiCallError ? err.details.error : 'Could not file that handout.');
        } finally {
            setBusyHandout(null);
        }
    }

    async function openOne(item: PrintItemDto) {
        try {
            const { url } = await getDocumentUrl(await getToken(), item.documentId);
            /* ⛔ ONE PER CLICK, NEVER A LOOP. See `buildPrintList`: a popup blocker
               allows the first window.open after a gesture and eats the rest
               silently, so a loop would open one and look like it worked. */
            window.open(url, '_blank', 'noopener');
            setOpened(prev => new Set(prev).add(item.documentId));
        } catch (err) {
            setError(err instanceof ApiCallError ? err.details.error : 'Could not open that document.');
        }
    }

    if (!appointmentId) return null;

    const list = data?.resolution?.checklist ?? null;

    /**
     * ---- ⭐ `hideWhenEmpty` — OPT-IN, AND THE DEFAULT IS UNCHANGED (2026-09-25)
     *
     * > Pete, on the Edit dialog: *"Before it leaves section needs to be removed."*
     * > Offered remove / move / hide-when-empty, he chose hide-when-empty.
     *
     * ⛔⛔ THIS OVERRIDES THE SAFETY NOTE BELOW, SO IT IS OPT-IN RATHER THAN THE
     * DEFAULT. That note is right and still stands for any surface that is deciding
     * whether a truck may leave: an empty checklist rendered as silence reads as a
     * pass, and "nobody configured this" is not "nothing is needed".
     *
     * ⭐ WHAT MAKES IT SAFE HERE: the EDIT DIALOG IS NOT THE GATE. Departure
     * requirements are enforced server-side in `outboundRelease.ts` and
     * `departureChecklist.ts`; this panel is a readout. Hiding a readout on a form
     * about scheduling does not let a truck out.
     *   ** A WARNING MAY BE HIDDEN ON A SCREEN THAT CANNOT ACT ON IT, PROVIDED THE
     *      SCREEN THAT CAN STILL SHOWS IT. **
     *
     * ⚠️ ERRORS ARE NEVER HIDDEN. A failed fetch is not an empty checklist, and
     * silently swallowing it would make a broken panel look like a configured-clean
     * one - which is the original note's fear wearing a different hat.
     */
    if (hideWhenEmpty && !error && !list) return null;
    const items = data?.assessment?.items ?? [];
    const handouts = data?.handouts ?? [];

    return (
        <div className="space-y-1.5">
            <div className="flex items-baseline justify-between gap-2">
                <span className="text-sm font-medium">Before it leaves</span>
                {loading && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
            </div>

            {error && (
                <div className="rounded border border-destructive/40 bg-destructive/10 px-2 py-1 text-[11px] text-destructive">
                    {error}
                </div>
            )}

            {/**
              * ⭐ "NOTHING IS CONFIGURED" IS SAID OUT LOUD, and it is not the same
              * sentence as "everything is done".
              *
              * ⛔ AN EMPTY LIST RENDERED AS SILENCE WOULD BE READ AS A PASS. That is
              * the single most dangerous thing this panel could do: a scheduler
              * glancing at a blank checklist concludes the load is clear, when in
              * fact nobody has ever told the system what the load needs.
              */}
            {!loading && !error && !list && (
                <div className="rounded border border-dashed px-2 py-2 text-[11px] text-muted-foreground">
                    {data?.reason
                        ?? 'No departure requirements are configured for this appointment. '
                         + 'That is not the same as "nothing is needed" — an admin sets these '
                         + 'up in Settings → Departure checklists.'}
                </div>
            )}

            {list && (
                <>
                    <div className="space-y-1">
                        {items.map((it) => {
                            const style = STATE_STYLE[it.state] ?? STATE_STYLE.missing;
                            const isStep = it.requirement.kind === 'step';
                            const tick = data?.stepTicks?.[it.requirement.requirementId];
                            return (
                                <div
                                    key={it.requirement.requirementId}
                                    className={'flex items-start gap-1.5 text-[11px] leading-snug '
                                        + (it.blocks ? BLOCKING_ROW : 'px-0.5')}
                                >
                                    {/**
                                      * ⛔ A CHECKBOX ONLY ON A STEP. See the header:
                                      * a box beside a derived requirement would be a
                                      * second truth that drifts, and the first person
                                      * to tick it would be recording a wish.
                                      */}
                                    {isStep ? (
                                        <button
                                            type="button"
                                            onClick={() => void toggleStep(it)}
                                            disabled={busyItem !== null}
                                            aria-pressed={it.state === 'met'}
                                            title={it.state === 'met'
                                                ? 'Untick this step'
                                                : 'Tick this step — your name is kept against it'}
                                            className="mt-px shrink-0 text-muted-foreground hover:text-foreground disabled:opacity-40"
                                        >
                                            {busyItem === it.requirement.requirementId
                                                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                : it.state === 'met'
                                                    ? <CheckSquare className="h-3.5 w-3.5 text-emerald-600" />
                                                    : <Square className="h-3.5 w-3.5" />}
                                        </button>
                                    ) : style.icon}

                                    <div className="min-w-0 flex-1">
                                        <span className={it.blocks ? 'font-medium text-amber-900' : style.text}>
                                            {it.message}
                                        </span>
                                        {/**
                                          * ⚠️ COPIES IS SHOWN AS AN INSTRUCTION, NEVER AS
                                          * A COUNT TO BE MET. Nothing here can observe two
                                          * sheets leaving a printer, so the wording is
                                          * "print 2" rather than "2 of 2".
                                          */}
                                        {it.requirement.copies > 1 && !isStep && (
                                            <span className="text-muted-foreground"> · print {it.requirement.copies}</span>
                                        )}
                                        {tick?.completedBy && (
                                            <span className="text-muted-foreground"> · {tick.completedBy}</span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/**
                      * ⭐ IT SAYS WHICH LIST IT USED, AND WHAT IT DISPLACED.
                      *
                      * ⚠️ Collapsed by default: the answer matters when somebody asks,
                      * and four scope levels of provenance on every load would bury the
                      * requirements themselves.
                      */}
                    <button
                        type="button"
                        onClick={() => setShowScope(v => !v)}
                        className="text-[10px] text-muted-foreground underline-offset-2 hover:underline"
                    >
                        {showScope ? 'Hide' : 'Why these?'}
                    </button>
                    {showScope && (
                        <div className="rounded border bg-muted/40 px-2 py-1.5 text-[10px] text-muted-foreground">
                            <div>
                                <span className="font-medium">{list.name}</span>
                                {' · '}{data?.direction === 'INBOUND' ? 'inbound' : 'outbound'}
                                {' · scope: '}{data?.resolution.scope || 'everything'}
                            </div>
                            {/**
                              * ⛔ THE LISTS DO NOT MERGE, AND THIS IS WHERE THAT BECOMES
                              * VISIBLE. Most specific wins outright — merging a
                              * company-wide list into a client's would silently add a
                              * requirement nobody writing the client's list had seen.
                              */}
                            {(data?.resolution.alsoMatched.length ?? 0) > 0 && (
                                <div className="mt-1">
                                    Also matched, and NOT used (the most specific list wins
                                    outright — they are not combined):
                                    <ul className="mt-0.5 list-disc pl-4">
                                        {data?.resolution.alsoMatched.map(m => (
                                            <li key={m.checklistId}>{m.name} — {m.scope}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}

                {/**
                  * ⭐ THE PRINT PACK — everything to hand over, with the counts.
                  *
                  * ⛔ ONE BUTTON PER DOCUMENT, NOT ONE "PRINT ALL". `buildPrintList`
                  * carries the reasoning: a browser opens the first popup after a
                  * user gesture and silently drops the rest, so a loop would print
                  * one of four and report success. Three extra clicks buys the
                  * guarantee that nothing is skipped without somebody seeing it.
                  */}
                {(data?.printList?.length ?? 0) > 0 && (
                    <div className="mt-2 border-t pt-1.5">
                        <button
                            type="button"
                            onClick={() => setShowPrint(v => !v)}
                            className="flex w-full items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground"
                        >
                            <Printer className="h-3 w-3" />
                            Print pack ({data?.printList?.reduce((n, i) => n + i.copies, 0)} sheets,
                            {' '}{data?.printList?.length} document{(data?.printList?.length ?? 0) === 1 ? '' : 's'})
                            <span className="ml-auto font-normal normal-case">{showPrint ? 'hide' : 'open'}</span>
                        </button>

                        {showPrint && (
                            <div className="mt-1 space-y-1">
                                {(data?.printList ?? []).map(item => (
                                    <div key={item.documentId} className="flex items-start gap-1.5 text-[11px] leading-snug">
                                        {opened.has(item.documentId)
                                            ? <Check className="mt-px h-3.5 w-3.5 shrink-0 text-emerald-600" />
                                            : <Printer className="mt-px h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                                        <div className="min-w-0 flex-1">
                                            <span className={opened.has(item.documentId) ? 'text-muted-foreground' : ''}>
                                                {item.reason}
                                            </span>
                                            <span className="font-medium"> × {item.copies}</span>
                                            <div className="truncate text-[10px] text-muted-foreground" title={item.fileName}>
                                                {item.fileName}
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => void openOne(item)}
                                            className="shrink-0 rounded border px-1.5 py-0.5 text-[10px] hover:bg-muted"
                                        >
                                            {opened.has(item.documentId) ? 'Open again' : 'Open'}
                                        </button>
                                    </div>
                                ))}
                                {/**
                                  * ⚠️ THE TICKS SAY "OPENED", NOT "PRINTED". Nothing here
                                  * can see a printer, and a green tick implying otherwise
                                  * would be the same false green `copies` has been kept
                                  * away from all along.
                                  */}
                                <p className="text-[10px] text-muted-foreground">
                                    A tick means the document was opened — nothing here can see the
                                    printer. The counts come from the checklist and the handout library.
                                </p>
                            </div>
                        )}
                    </div>
                )}

                {/**
                  * ⭐ #317 STEP 5 — WHAT TO HAND HIM, from the library.
                  *
                  * ⛔⛔ OUTSIDE THE `list &&` GUARD, AND THE FIRST DRAFT HAD IT INSIDE.
                  * That would have hidden every handout on any load with no configured
                  * checklist — which is MOST loads, and precisely the case a
                  * client-wide safety sheet exists for. The two features are related,
                  * not nested: a handout does not need a requirement to be worth
                  * handing over.
                  *
                  * ⛔ A SEPARATE SECTION FROM THE REQUIREMENTS ABOVE, AND THAT IS
                  * NOT A LAYOUT CHOICE. The list above says what is MISSING; this
                  * says what is AVAILABLE. They pair up by document type most of
                  * the time, and when they do not, both facts matter: a
                  * requirement with no handout is an admin asking for something
                  * the library cannot supply, and a handout with no requirement is
                  * a client-wide sheet somebody may still want to give him.
                  *
                  * ⚠️ NOTHING IS FILED AUTOMATICALLY. Auto-attaching would put
                  * whatever revision happened to be current onto a load without
                  * anybody looking at it — and "which directions did he get" then
                  * has an answer nobody chose.
                  */}
                {(handouts.length > 0 || data?.handoutsUnavailable) && (
                    <div className="mt-2 space-y-1 border-t pt-1.5">
                        <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                            To hand over
                        </div>

                        {/* ⛔ A FAILED READ IS NOT AN EMPTY LIBRARY. Telling a
                            scheduler there is nothing to hand over, confidently and
                            wrongly, is the worse of the two. */}
                        {data?.handoutsUnavailable && (
                            <div className="text-[10px] text-amber-700">
                                The handout library could not be read, so anything this client
                                normally receives is not listed here.
                            </div>
                        )}

                        {handouts.map(h => {
                            const filed = h.revision
                                && h.filedRevisions.includes(h.revision.revision);
                            /* ⚠️ "OLDER REVISION ALREADY ON THE LOAD" IS ITS OWN
                               STATE. He was given something — just not what the
                               library now says. Showing it as un-filed would invite
                               a duplicate; showing it as done would hide that the
                               directions have changed since. */
                            const stale = !filed && h.filedRevisions.length > 0;
                            return (
                                <div
                                    key={h.handout.handoutId}
                                    className="flex items-start gap-1.5 px-0.5 text-[11px] leading-snug"
                                >
                                    {filed
                                        ? <Check className="mt-px h-3.5 w-3.5 shrink-0 text-emerald-600" />
                                        : <FileText className="mt-px h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                                    <div className="min-w-0 flex-1">
                                        <span className={filed ? 'text-muted-foreground' : ''}>
                                            {h.handout.name}
                                        </span>
                                        {h.handout.copies > 1 && (
                                            <span className="text-muted-foreground"> · print {h.handout.copies}</span>
                                        )}
                                        {/* ⭐ THE REVISION IS NAMED. It is what the load's
                                            record cites, and what makes "which directions
                                            did he get" answerable rather than merely
                                            evidenced. */}
                                        {filed && h.revision && (
                                            <span className="text-muted-foreground"> · given (rev {h.revision.revision})</span>
                                        )}
                                        {stale && h.revision && (
                                            <span className="text-amber-700">
                                                {' '}· he has rev {h.filedRevisions.join(', ')}; the current one is {h.revision.revision}
                                            </span>
                                        )}
                                        {!h.revision && (
                                            <span className="text-amber-700"> · no file uploaded yet</span>
                                        )}
                                    </div>
                                    {/* ⛔ NO BUTTON WITHOUT A REVISION OR A FACILITY. An
                                        action that cannot succeed should not be offered —
                                        the sentence beside it already says why. */}
                                    {h.revision && facilityCode && !filed && (
                                        <button
                                            type="button"
                                            onClick={() => void doFile(h)}
                                            disabled={busyHandout !== null}
                                            className="shrink-0 rounded border px-1.5 py-0.5 text-[10px] hover:bg-muted disabled:opacity-40"
                                        >
                                            {busyHandout === h.handout.handoutId
                                                ? 'Filing…'
                                                : stale ? 'Give the new one' : 'Add it'}
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
        </div>
    );
}
